import { RightOutlined } from '@ant-design/icons'
import { Checkbox, Collapse } from 'antd'
import { useEffect, useMemo, useState } from 'react'

import { CharacteristicFilterType } from '@/types'

import FilterOptionPills, {
  FilterOptionPillItem,
} from '../FilterOptionPills/FilterOptionPills'
import {
  formatCharacteristicValue,
  pinSelectedOptions,
  shouldRenderAsPills,
  toggleCharacteristicOptionValue,
} from '../Filters.utils'
import generalCn from '../GeneralFiltersStyles.module.scss'
import HighlightText from '../HighlightText/HighlightText'
import cn from './FilterByCodeWithList.module.scss'

const EMPTY_VALUES: string[] = []

type FilterByCodeWithListPropsType = {
  item: CharacteristicFilterType
  selectedItems: CharacteristicFilterType[]
  title: string
  collapseItemKey: string
  setSelectedItems: (selectedItems: CharacteristicFilterType[]) => void
  forceOpen?: boolean
  searchQuery?: string
  pinnedValues?: string[]
}

function FilterByCodeWithList(props: FilterByCodeWithListPropsType) {
  const {
    item,
    selectedItems,
    title,
    collapseItemKey,
    setSelectedItems,
    forceOpen,
    searchQuery = '',
    pinnedValues = EMPTY_VALUES,
  } = props

  const isApplied = selectedItems.some((sel) => sel.code === item.code)
  const [isOpen, setIsOpen] = useState(isApplied)

  useEffect(() => {
    if (!isApplied) setIsOpen(false)
  }, [isApplied])

  const displayOptions = useMemo(() => {
    const optionsMap = new Map<string, { value: string; count: number }>()

    pinSelectedOptions(item.options, pinnedValues).forEach((opt) => {
      optionsMap.set(opt.value, opt)
    })

    const currentSelectedChar = selectedItems.find(
      (sel) => sel.code === item.code
    )

    if (currentSelectedChar) {
      const currentValues = [
        ...(currentSelectedChar.options?.map((o) => String(o.value)) || []),
        ...(currentSelectedChar.chosenOptions || []),
      ]

      currentValues.forEach((val) => {
        if (!optionsMap.has(val)) {
          optionsMap.set(val, { value: val, count: 0 })
        }
      })
    }

    return Array.from(optionsMap.values())
  }, [item.options, selectedItems, item.code, pinnedValues])

  const selectedCount = (() => {
    const selectedChar = selectedItems.find((sel) => sel.code === item.code)
    if (!selectedChar) return 0
    return new Set([
      ...(selectedChar.options?.map((o) => String(o.value)) || []),
      ...(selectedChar.chosenOptions || []),
    ]).size
  })()

  const usesPills = shouldRenderAsPills(displayOptions.length)

  const pillItems: FilterOptionPillItem[] = displayOptions.map((option) => ({
    key: option.value,
    label: (
      <HighlightText
        text={`${formatCharacteristicValue(option.value)}${
          item.unitOfMeasurementDto?.description
            ? ` ${item.unitOfMeasurementDto.description}`
            : ''
        }`}
        query={searchQuery}
      />
    ),
    selected: selectedItems.some((sel) => {
      if (sel.code !== item.code) return false
      return (
        sel.chosenOptions?.includes(option.value) ||
        sel.options?.some((opt) => String(opt.value) === String(option.value))
      )
    }),
    disabled: option.count === 0,
    count: option.count,
  }))

  return (
    <div className={generalCn.filterContainer}>
      <div className={generalCn.collapseContainer}>
        <Collapse
          activeKey={forceOpen || isOpen ? [collapseItemKey] : []}
          onChange={() => {
            if (!forceOpen) setIsOpen((prev) => !prev)
          }}
          expandIcon={({ isActive }) => (
            <RightOutlined
              className={`${generalCn.expandIcon} ${isActive ? generalCn.expandIconActive : ''}`}
            />
          )}
          items={[
            {
              key: collapseItemKey,
              label: (
                <div className={generalCn.collapseHeaderContainer}>
                  <span>
                    <HighlightText text={title} query={searchQuery} />
                  </span>
                  {selectedCount > 0 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setSelectedItems(
                          selectedItems.filter((sel) => sel.code !== item.code)
                        )
                      }}
                    >
                      Сбросить
                    </button>
                  )}
                </div>
              ),
              children: usesPills ? (
                <div className={generalCn.collapseItemContent}>
                  <FilterOptionPills
                    options={pillItems}
                    onToggle={(value) =>
                      setSelectedItems(
                        toggleCharacteristicOptionValue(
                          selectedItems,
                          item,
                          value
                        )
                      )
                    }
                  />
                </div>
              ) : (
                <div className={generalCn.collapseItemContent}>
                  <div className={cn.list}>
                    {displayOptions.map((option) => (
                      <div
                        className={
                          option.count === 0
                            ? [cn.listItem, cn.listItemDisabled].join(' ')
                            : cn.listItem
                        }
                        key={option.value}
                      >
                        <div className={cn.checkboxContainer}>
                          <Checkbox
                            onChange={() =>
                              setSelectedItems(
                                toggleCharacteristicOptionValue(
                                  selectedItems,
                                  item,
                                  option.value
                                )
                              )
                            }
                            checked={selectedItems.some((sel) => {
                              if (sel.code !== item.code) return false
                              return (
                                sel.chosenOptions?.includes(option.value) ||
                                sel.options?.some(
                                  (opt) =>
                                    String(opt.value) === String(option.value)
                                )
                              )
                            })}
                          />
                        </div>
                        <div className={cn.text}>
                          <span>
                            <HighlightText
                              text={`${formatCharacteristicValue(option.value)}${
                                item.unitOfMeasurementDto?.description
                                  ? ` ${item.unitOfMeasurementDto.description}`
                                  : ''
                              }`}
                              query={searchQuery}
                            />
                          </span>
                          <span>({option.count})</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ),
            },
          ]}
        />
      </div>
    </div>
  )
}

export default FilterByCodeWithList
